import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import BannerService from "../../Services/BannerService";

export default function EditBanner({
  show,
  onHide,
  onSuccess,
  bannerData,
  pageTitle = "Banner",
}) {
  const imageRef = useRef(null);
  const [editData, setEditData] = useState({
    imageFile: null,
    currentImagePath: "",
    title: "",
    description: "",
    link: "",
  });
  const [validated, setValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (bannerData && show) {
      setEditData({
        imageFile: null,
        currentImagePath: bannerData.imagePath || "",
        title: bannerData.title || "",
        description: bannerData.description?.replace(/\\n/g, "\n") || "",
        link: bannerData.link || "",
      });
    }
  }, [bannerData, show]);

  const handleImageInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG)");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setEditData((prev) => ({
      ...prev,
      imageFile: file,
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Updating...");

      const formData = new FormData();

      if (editData.imageFile) {
        formData.append("image", editData.imageFile);
      }

      const processedTitle = editData.title.trim().replace(/\r?\n/g, "\\n");
      const processedDescription = editData.description
        .trim()
        .replace(/\r?\n/g, "\\n");
      formData.append("title", processedTitle);
      formData.append("description", processedDescription);

      const response = await BannerService.updateBanner(
        bannerData.id,
        formData
      );

      if (response.data.success) {
        toast.success(response.data.message, { id: loadingMessage });
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(response.data.message || "Something went wrong", {
          id: loadingMessage,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setValidated(false);
    setEditData({
      imageFile: null,
      currentImagePath: "",
      title: "",
      description: "",
      link: "",
    });
    onHide();
  };

  const handleRemoveNewImage = () => {
    setEditData((prev) => ({
      ...prev,
      imageFile: null,
    }));
    // Reset the file input
    if (imageRef.current) {
      imageRef.current.value = "";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="lg">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          <i className="bi bi-pencil-square me-2"></i>
          Edit {pageTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            {/* Title */}
            <Col xs={12} md={6}>
              <Form.Group controlId="title">
                <Form.Label className="form-label fs-5">Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={editData.title}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                  required
                  isInvalid={validated && !editData.title.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a title
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Link */}
            {/* <Col xs={12} md={6}>
              <Form.Group controlId="link">
                <Form.Label className="form-label fs-5">
                  Link (Optional)
                </Form.Label>
                <Form.Control
                  type="url"
                  name="link"
                  value={editData.link}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="form-control fs-5"
                />
              </Form.Group>
            </Col> */}

            {/* Description */}
            <Col xs={12} md={12}>
              <Form.Group controlId="description">
                <Form.Label className="form-label fs-5">
                  Description *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={editData.description}
                  onChange={handleInputChange}
                  placeholder="Enter banner description"
                  required
                  isInvalid={validated && !editData.description.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Image */}
            <Col xs={12} md={12}>
              <Form.Label className="form-label fs-5">Banner Image</Form.Label>

              {/* Show current image */}
              {editData.currentImagePath && !editData.imageFile && (
                <div className="mt-2">
                  <p className="text-muted small mb-2">Current Image:</p>
                  <img
                    src={editData.currentImagePath}
                    alt="Current Banner"
                    style={{ maxWidth: "90%", maxHeight: "200px" }}
                    className="img-fluid"
                  />
                </div>
              )}

              {/* Show new image preview */}
              {editData.imageFile ? (
                <div className="mt-2">
                  <p className="text-muted small mb-2">New Image:</p>
                  <div className="d-flex position-relative">
                    <img
                      src={URL.createObjectURL(editData.imageFile)}
                      alt="New Upload"
                      style={{ maxWidth: "90%", maxHeight: "200px" }}
                      className="img-fluid"
                    />
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{
                        position: "absolute",
                        right: "35px",
                        top: "-13px",
                      }}
                      onClick={handleRemoveNewImage}
                    >
                      <i className="bi bi-x-circle-fill text-danger"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label
                    htmlFor="importInput_image_edit"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-cloud-plus me-2"></i> Upload New Image
                  </label>
                  <input
                    id="importInput_image_edit"
                    type="file"
                    className="d-none"
                    ref={imageRef}
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageInput}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to keep current image.
                  </Form.Text>
                </div>
              )}
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Update Banner
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
